import { useState, useEffect } from 'react'
import { UserPlus, Edit2, X, RefreshCw, UserX, UserCheck, Mail } from 'lucide-react'
import { useAuthStore } from '../store/authStore'

const UserManagement = () => {
  const [users, setUsers] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const { token } = useAuthStore()

  const [newUser, setNewUser] = useState({
    correo: '',
    nombre: '',
    programa: '',
    rol: 'usuario',
    nivel: 'ambos'
  })

  useEffect(() => {
    fetchUsers()
  }, [])

  const fetchUsers = async () => {
    setIsLoading(true)
    try {
      const baseUrl = process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : ''
      const response = await fetch(`${baseUrl}/api/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) throw new Error('Error al cargar usuarios')

      const data = await response.json()
      setUsers(data.users)
    } catch (err) {
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreateUser = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)

    try {
      const baseUrl = process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : ''
      const response = await fetch(`${baseUrl}/api/admin/create-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newUser)
      })

      const data = await response.json()

      if (!response.ok) throw new Error(data.error)

      // Mostrar mensaje según si el email se envió o no
      let successMessage = 'Usuario creado exitosamente. '
      if (data.emailSent) {
        successMessage += `✉️ Contraseña temporal enviada a ${data.user.correo}`
      } else {
        successMessage += `⚠️ El correo no pudo ser enviado. Por favor, contacta al usuario directamente para proporcionarle acceso al sistema.`
      }
      
      setSuccess(successMessage)
      setShowCreateModal(false)
      setNewUser({ correo: '', nombre: '', programa: '', rol: 'usuario', nivel: 'ambos' })
      fetchUsers()
    } catch (err) {
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleUpdateUser = async (userId, updates) => {
    setIsLoading(true)
    setError(null)

    try {
      const baseUrl = process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : ''
      const response = await fetch(`${baseUrl}/api/admin/update-user`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ userId, ...updates })
      })

      if (!response.ok) throw new Error('Error al actualizar usuario')

      setSuccess('Usuario actualizado exitosamente')
      setEditingUser(null)
      fetchUsers()
    } catch (err) {
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }

  const toggleUserStatus = async (userId, currentStatus) => {
    setIsLoading(true)
    setError(null)

    try {
      const baseUrl = process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : ''
      const response = await fetch(`${baseUrl}/api/admin/toggle-user-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          userId, 
          activo: !currentStatus 
        })
      })

      const data = await response.json()

      if (!response.ok) throw new Error(data.error)

      setSuccess(data.message)
      fetchUsers()
    } catch (err) {
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }

  const resendPassword = async (userId, userEmail) => {
    if (!confirm(`¿Estás seguro de que deseas reenviar la contraseña a ${userEmail}?`)) {
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const baseUrl = process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : ''
      const response = await fetch(`${baseUrl}/api/admin/resend-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ userId })
      })

      const data = await response.json()

      if (!response.ok) throw new Error(data.error)

      if (data.emailSent) {
        setSuccess(`✉️ Nueva contraseña enviada a ${userEmail}`)
      } else {
        setSuccess(`⚠️ Contraseña restablecida pero el correo no pudo ser enviado. Contraseña temporal: ${data.temporaryPassword}`)
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Gestión de Usuarios</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary inline-flex items-center space-x-2"
        >
          <UserPlus className="h-4 w-4" />
          <span>Crear Usuario</span>
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {success && (
        <div className="p-3 bg-green-100 border border-green-400 text-green-700 rounded">
          {success}
          <button onClick={() => setSuccess(null)} className="float-right">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {isLoading && !users.length ? (
        <div className="text-center py-8">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto text-gray-400" />
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Correo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Programa</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rol</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map(user => (
                <tr key={user.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">{user.correo}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">{user.nombre}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">{user.programa}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded ${
                      user.rol === 'admin' ? 'bg-purple-100 text-purple-800' :
                      user.rol === 'visualizador' ? 'bg-blue-100 text-blue-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {user.rol}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-1 text-xs rounded ${
                      user.activo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {user.activo ? (
                        <>
                          <UserCheck className="h-3 w-3 mr-1" />
                          Activo
                        </>
                      ) : (
                        <>
                          <UserX className="h-3 w-3 mr-1" />
                          Suspendido
                        </>
                      )}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setEditingUser(user)}
                        className="text-blue-600 hover:text-blue-800"
                        title="Editar usuario"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => resendPassword(user.id, user.correo)}
                        className="text-purple-600 hover:text-purple-800"
                        title="Reenviar contraseña"
                      >
                        <Mail className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => toggleUserStatus(user.id, user.activo)}
                        className={`${
                          user.activo 
                            ? 'text-red-600 hover:text-red-800' 
                            : 'text-green-600 hover:text-green-800'
                        }`}
                        title={user.activo ? 'Suspender usuario' : 'Activar usuario'}
                      >
                        {user.activo ? (
                          <UserX className="h-4 w-4" />
                        ) : (
                          <UserCheck className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal Editar Usuario */}
      {editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Editar Usuario</h3>
            <form onSubmit={(e) => {
              e.preventDefault()
              handleUpdateUser(editingUser.id, {
                nombre: editingUser.nombre,
                programa: editingUser.programa,
                rol: editingUser.rol,
                nivel: editingUser.nivel
              })
            }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Correo</label>
                <input
                  type="email"
                  disabled
                  className="input-field mt-1 bg-gray-100"
                  value={editingUser.correo}
                />
                <p className="text-xs text-gray-500 mt-1">El correo no se puede modificar</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Nombre</label>
                <input
                  type="text"
                  required
                  className="input-field mt-1"
                  value={editingUser.nombre}
                  onChange={(e) => setEditingUser({...editingUser, nombre: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Programa</label>
                <input
                  type="text"
                  required
                  className="input-field mt-1"
                  value={editingUser.programa}
                  onChange={(e) => setEditingUser({...editingUser, programa: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Rol</label>
                <select
                  className="input-field mt-1"
                  value={editingUser.rol}
                  onChange={(e) => setEditingUser({...editingUser, rol: e.target.value})}
                >
                  <option value="usuario">Usuario</option>
                  <option value="admin">Administrador</option>
                  <option value="visualizador">Visualizador</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Nivel</label>
                <select
                  className="input-field mt-1"
                  value={editingUser.nivel}
                  onChange={(e) => setEditingUser({...editingUser, nivel: e.target.value})}
                >
                  <option value="ambos">Ambos</option>
                  <option value="pregrado">Pregrado</option>
                  <option value="postgrado">Postgrado</option>
                </select>
              </div>
              <div className="flex space-x-3">
                <button type="submit" disabled={isLoading} className="btn-primary flex-1">
                  {isLoading ? 'Guardando...' : 'Guardar Cambios'}
                </button>
                <button
                  type="button"
                  onClick={() => setEditingUser(null)}
                  className="btn-secondary flex-1"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Crear Usuario */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Crear Nuevo Usuario</h3>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Correo</label>
                <input
                  type="email"
                  required
                  className="input-field mt-1"
                  value={newUser.correo}
                  onChange={(e) => setNewUser({...newUser, correo: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Nombre</label>
                <input
                  type="text"
                  required
                  className="input-field mt-1"
                  value={newUser.nombre}
                  onChange={(e) => setNewUser({...newUser, nombre: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Programa</label>
                <input
                  type="text"
                  required
                  className="input-field mt-1"
                  value={newUser.programa}
                  onChange={(e) => setNewUser({...newUser, programa: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Rol</label>
                <select
                  className="input-field mt-1"
                  value={newUser.rol}
                  onChange={(e) => setNewUser({...newUser, rol: e.target.value})}
                >
                  <option value="usuario">Usuario</option>
                  <option value="admin">Administrador</option>
                  <option value="visualizador">Visualizador</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Nivel</label>
                <select
                  className="input-field mt-1"
                  value={newUser.nivel}
                  onChange={(e) => setNewUser({...newUser, nivel: e.target.value})}
                >
                  <option value="ambos">Ambos</option>
                  <option value="pregrado">Pregrado</option>
                  <option value="postgrado">Postgrado</option>
                </select>
              </div>
              <div className="flex space-x-3">
                <button type="submit" disabled={isLoading} className="btn-primary flex-1">
                  {isLoading ? 'Creando...' : 'Crear Usuario'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserManagement
